/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { PERMISSIONS } from '@squiz/dxp-auth';
import {
  DataList,
  DataListItem,
  EmptyState,
  ICON_CHEVRON_RIGHT,
  ICON_FOLDER_ZIP_WITH_LIST,
  SPINNER_TYPE_REGULAR,
  SdsHeading,
  SdsIcon,
  SdsText,
  SdsTextSize,
  SdsTextWeight,
  Spinner,
} from '@squiz/sds';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import { hasPermission } from 'apps/console-core/src/Utilities/HasPermission/HasPermission';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CONFIGURATION_STRINGS } from '../../../../Constants/Strings.constants';
import { DAM_INTEGRATIONS_DATA_IDS } from '../../DamIntegrations.constants';
import { useDamIntegrations } from '../../hooks/UseDamIntegrations/UseDamIntegrations.hook';
import { DamIntegrationPublic } from '../../types/DamIntegrationsPublic/DamIntegrationsPublic';
import { AddDamButton } from '../AddDamButton/AddDamButton';
import { DamIntegrationActions } from '../DamIntegrationActions/DamIntegrationActions';
import { DamStatusBadge } from '../DamStatusBadge/DamStatusBadge';

/**
 * The DAM integration list component.
 */
export const DamIntegrationList = (): JSX.Element => {
  const { t } = useTranslation();

  const [integrationData, error] = useDamIntegrations();

  // Handle navigation
  const navigate = useNavigate();
  const handleNavigation = useCallback(
    (itemPath: string): void => {
      navigate(itemPath);
    },
    [navigate],
  );

  // Permissions
  // Needs update permission since all integrations are stored in a single setting object
  const hasCreatePermission =
    hasPermission(PERMISSIONS.dxpDAMIntegrations.TENANT_SETTING_CREATE) &&
    hasPermission(PERMISSIONS.dxpDAMIntegrations.TENANT_SETTING_UPDATE);

  // Labels
  const labels = useMemo(() => {
    return {
      addDam: t(CONFIGURATION_STRINGS.addDam),
      description: t(CONFIGURATION_STRINGS.damIntegrationListDescription),
      disabled: t(CONFIGURATION_STRINGS.disabled),
      enabled: t(CONFIGURATION_STRINGS.enabled),
      heading: t(CONFIGURATION_STRINGS.currentDamIntegrations),
      noDamIntegrations: t(CONFIGURATION_STRINGS.noDamIntegrations),
    };
  }, [t]);

  const convertIntegrationToDataListItem = useCallback(
    (integration: DamIntegrationPublic): DataListItem => {
      const { enabled, name, uuid } = integration;

      const gotToDetailsPage = (): void => {
        const integrationDetailsPath = getCapabilityPath(`/dam-integrations/${uuid}`);
        handleNavigation(integrationDetailsPath);
      };

      const itemContent = (
        <div className="dam-integration-item__content">
          <SdsText fontSize={SdsTextSize.Large} fontWeight={SdsTextWeight.Semibold} text={name} />
          <SdsIcon classes="dam-integration-item__icon" svg={ICON_CHEVRON_RIGHT} />
          <DamStatusBadge enabled={enabled} />
        </div>
      );

      return {
        action: gotToDetailsPage,
        content: itemContent,
        dataId: DAM_INTEGRATIONS_DATA_IDS.damIntegrationListButton,
        id: uuid,
        isCustom: true,
        key: uuid,
      };
    },
    [handleNavigation],
  );

  // Loading state
  if (!integrationData && !error) {
    return (
      <div className="loading">
        <Spinner type={SPINNER_TYPE_REGULAR} />
      </div>
    );
  }

  const getIntegrationList = (): JSX.Element => {
    if (error || !integrationData || !integrationData.integrations.length) {
      return (
        <EmptyState
          caption={labels.noDamIntegrations}
          classes="dam-integration-list__empty sds-empty-state-card--with-cta"
          dataId={DAM_INTEGRATIONS_DATA_IDS.emptyState}
          isLite
          svg={ICON_FOLDER_ZIP_WITH_LIST}
        >
          {hasCreatePermission && <AddDamButton tenantSetting={integrationData?.tenantSetting} />}
        </EmptyState>
      );
    }

    const integrationItems = integrationData.integrations.map((integration) =>
      convertIntegrationToDataListItem(integration),
    );

    return <DataList empty={<></>} isInteractive items={integrationItems} showHeader={false} />;
  };

  return (
    <>
      <DamIntegrationActions hasCreatePermission={hasCreatePermission} tenantSetting={integrationData?.tenantSetting} />
      <div className="dam-integration-list__wrapper">
        <SdsHeading classes="dam-integration-list__heading" level={4} text={labels.heading} />
        <SdsText classes="dam-integration-list__description" fontSize={SdsTextSize.Small} text={labels.description} />
        {getIntegrationList()}
      </div>
    </>
  );
};
